import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useRouter } from 'next/router';
import { normalizeText } from '@portal-internet/core';
import { getUrlFriendly } from 'components/3catinfo/Enllac/RouteResolver';
import { useViewport } from 'hooks/3catinfo/useViewport';
import Icona from 'components/3catinfo/ui/Icona';
import Enllac from 'components/3catinfo/Enllac';
import stylesDefault from './cercaMunicipi.module.scss';
import __isEmpty from 'lodash/isEmpty';
import municipisDefault from './municipis.json';
import clsx from 'clsx';

const filterMunicipi = (searchValue, municipisList) => {
    if (!municipisList) return [];
    return municipisList.filter(({ nom }) =>
        normalizeText(nom)?.toLowerCase().includes(normalizeText(searchValue).toLowerCase())
    );
};

const ItemResultatCerca = forwardRef((props, publicRef) => {
    const { item, index, handleKeyDown, routePath, getTitle, marcatgeAudienciesClick, styles } = props;
    const ref = useRef();
    const { nom, id, detall } = item;

    useImperativeHandle(publicRef, () => ({
        focus() {
            ref.current.focus();
        }
    }));

    return (
        <li className={styles.itemResultat} ref={ref} tabIndex={index} onKeyDown={handleKeyDown}>
            <Enllac
                type={routePath}
                slug={nom}
                id={id}
                className={styles.municipi}
                title={getTitle(item)}
                marcatgeAudienciesClick={marcatgeAudienciesClick ?? ''}
            >
                {nom}
                {detall}
            </Enllac>
        </li>
    );
});

const Resultats = (props) => {
    const { items, searching, setSearching, selectedIndex, handleKeyDown, routePath, styles, getTitle, marcatgeAudienciesClick } =
        props;
    const divRef = useRef(null);
    const [itemRefs, setItemRefs] = useState([]);

    const handleClickOutside = (event) => {
        if (divRef.current && !divRef.current.contains(event.target)) {
            setSearching(false);
        }
    };

    useEffect(() => {
        if (selectedIndex >= 0 && itemRefs[selectedIndex]) {
            itemRefs[selectedIndex].current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);

    useEffect(() => {
        setItemRefs((elRefs) =>
            Array(items.length)
                .fill()
                .map((_, i) => elRefs[i] || React.createRef())
        );
    }, [items]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!searching) return <></>;
    return (
        <div className={styles.wrapperResultats} ref={divRef}>
            <ul className={styles.contentResultats}>
                {__isEmpty(items) ? (
                    <li className={styles.noResults}>No s&apos;han trobat resultats</li>
                ) : (
                    items.map((item, index) => (
                        <ItemResultatCerca
                            key={`cercador-item-resultat-${item.id}`}
                            ref={itemRefs[index]}
                            item={item}
                            index={index}
                            handleKeyDown={handleKeyDown}
                            routePath={routePath}
                            getTitle={getTitle}
                            marcatgeAudienciesClick={marcatgeAudienciesClick}
                            styles={styles}
                        />
                    ))
                )}
            </ul>
        </div>
    );
};

const CercaMunicipi = (props) => {
    const {
        inputId = 'search',
        placeholder = 'Cerca',
        routePath = 'web_3catinfo_el_temps_municipi',
        getTitle,
        marcatgeAudienciesClick,
        estil,
        estilIcon,
        municipisList = municipisDefault?.municipis,
        mostrarDesplegable = true,
        isIframe = false
    } = props;

    const styles = props.styles || stylesDefault;
    const router = useRouter();
    const { zoomNavigator } = useViewport();

    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [searching, setSearching] = useState(false);
    const [isMouseFocus, setIsMouseFocus] = useState(false);
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
    const updateCercaTimer = useRef(null);
    const inputRef = useRef(null);
    const debounceTimer = 300;

    const stylesInnerSearcher = clsx({
        [styles.innerSearcher]: true,
        [styles.ambZoom]: zoomNavigator > 3
    });

    const stylesSearchInput = clsx({
        [styles.searchInput]: true,
        [styles[estil]]: !!estil,
        [styles.searchInputActive]: isMouseFocus,
        [styles.searchInputFocus]: isKeyboardFocus
    });

    const stylesButton = clsx(styles.searchButton, {
        [styles[estil]]: !!estil,
        [styles[estilIcon]]: !!estilIcon
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const list = filterMunicipi(searchValue, municipisList);

        if (searchValue.length >= 2 && !__isEmpty(list)) {
            const selected = list[selectedIndex === -1 ? 0 : selectedIndex];
            const urlDesti = getUrlFriendly({
                routePath: routePath,
                id: selected.id,
                slug: selected.nom,
                producte: 'web_noticies'
            });
            isIframe ? (window.top.location.href = urlDesti) : router.push(urlDesti);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, searchResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prevIndex) => {
                if (prevIndex === 0) {
                    inputRef.current.focus();
                    requestAnimationFrame(() => {
                        const length = inputRef.current.value.length;
                        inputRef.current.setSelectionRange(length, length);
                    });
                }
                return Math.max(prevIndex - 1, -1);
            });
        } else if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    useEffect(() => {
        clearTimeout(updateCercaTimer.current);

        if (searchValue.length >= 2) {
            updateCercaTimer.current = setTimeout(() => {
                setSelectedIndex(-1);
                setSearchResults(filterMunicipi(searchValue, municipisList));
                setSearching(true);
            }, debounceTimer);
        } else {
            setSearching(false);
        }
    }, [searchValue, municipisList]);

    return (
        <div className={stylesInnerSearcher}>
            <form onSubmit={handleSubmit} className={styles.searchForm}>
                <div className={styles.contentInput}>
                    <label htmlFor={inputId} className='sr-only'>
                        Buscar:
                    </label>
                    <input
                        onMouseDown={() => {
                            setIsMouseFocus(true);
                            setIsKeyboardFocus(false);
                        }}
                        onBlur={() => {
                            setIsMouseFocus(false);
                            setIsKeyboardFocus(false);
                        }}
                        onKeyDown={handleKeyDown}
                        onKeyUp={(e) => {
                            if (e.key === 'Tab' || (e.shiftKey && e.key === 'Tab')) {
                                setIsMouseFocus(false);
                            }
                        }}
                        onFocus={(e) => {
                            if (!isMouseFocus) {
                                setIsKeyboardFocus(true);
                            }
                        }}
                        ref={inputRef}
                        id={inputId}
                        type='search'
                        name='search'
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder={placeholder}
                        required
                        className={stylesSearchInput}
                        aria-label={placeholder}
                        autoComplete='off'
                        data-testid='cerca-municipi-input'
                        aria-haspopup='listbox'
                        aria-autocomplete='list'
                    />
                    <button type='submit' className={stylesButton} aria-label='buscar'>
                        <Icona className={styles.icona} icona='cercador_ple' isBlanc={false} inline={true} />
                    </button>
                </div>
                {mostrarDesplegable && (
                    <Resultats
                        searching={searching}
                        searchValue={searchValue}
                        items={searchResults}
                        selectedIndex={selectedIndex}
                        setSearching={setSearching}
                        handleKeyDown={handleKeyDown}
                        routePath={routePath}
                        getTitle={getTitle}
                        marcatgeAudienciesClick={marcatgeAudienciesClick}
                        styles={styles}
                    />
                )}
            </form>
        </div>
    );
};

export default CercaMunicipi;

ItemResultatCerca.displayName = 'ItemResultatCerca';
